import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Icon from 'components/AppIcon';
import { useAuth } from 'contexts/AuthContext';
import ServiceSelectionModal from 'components/modals/ServiceSelectionModal';
import AssignmentSubmissionForm from 'components/modals/AssignmentSubmissionForm';
import ClassManagementForm from 'components/modals/ClassManagementForm';
import WhatsAppIcon from 'components/icons/WhatsAppIcon';

const CTASection = () => {
  const { isLoggedIn } = useAuth();
  const navigate = useNavigate();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [onlineExperts, setOnlineExperts] = useState(0);
  const [todayOrders, setTodayOrders] = useState(0);

  // Modal states
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [showAssignmentForm, setShowAssignmentForm] = useState(false);
  const [showClassManagementForm, setShowClassManagementForm] = useState(false);

  // Handle service selection from the modal
  const handleServiceSelection = (serviceId) => {
    setShowServiceModal(false);

    // Show the appropriate form based on the selected service
    if (serviceId === 'assignment') {
      setShowAssignmentForm(true);
    } else if (serviceId === 'class-management') {
      setShowClassManagementForm(true);
    } else if (serviceId === 'consultation') {
      // Handle consultation request (e.g., open WhatsApp)
      const phoneNumber = '254793863320';
      const message = encodeURIComponent('Hi! I would like to request a consultation about your academic services. Can we discuss my specific needs?');
      window.open(`https://wa.me/${phoneNumber}?text=${message}`, '_blank');
    }
  };

  // Handle button clicks with authentication check
  const handleButtonClick = (action) => {
    if (!isLoggedIn) {
      // Redirect to login page
      navigate('/login');
      return;
    }

    // User is logged in, perform the action
    switch (action) {
      case 'assignment':
        setShowAssignmentForm(true);
        break;
      case 'class-management':
        setShowClassManagementForm(true);
        break;
      default:
        setShowServiceModal(true);
    }
  };

  useEffect(() => {
    // Update current time
    const timeTimer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    // Animate counters
    const expertsTimer = setInterval(() => {
      setOnlineExperts(prev => prev < 89 ? prev + 2 : 89);
    }, 100);

    const ordersTimer = setInterval(() => {
      setTodayOrders(prev => prev < 127 ? prev + 3 : 127);
    }, 120);

    return () => {
      clearInterval(timeTimer);
      clearInterval(expertsTimer);
      clearInterval(ordersTimer);
    };
  }, []);

  const contactOptions = [
    {
      platform: "Assignment Help",
      description: "Submit your assignment details",
      responseTime: "2 minutes",
      icon: "FileText",
      color: "accent",
      action: () => handleButtonClick('assignment')
    },
    {
      platform: "Class Management",
      description: "Let us handle your entire course",
      responseTime: "Active now",
      icon: "BookOpen",
      color: "primary",
      action: () => handleButtonClick('class-management')
    },
    {
      platform: "WhatsApp",
      description: "Chat with our support team",
      responseTime: "1 hour",
      icon: "MessageCircle",
      color: "secondary",
      action: () => {
        const phoneNumber = '254793863320';
        const message = encodeURIComponent('Hi! I need help with my assignment. Can you provide more details about your services?');
        window.open(`https://wa.me/${phoneNumber}?text=${message}`, '_blank');
      }
    }
  ];

  const urgencyOptions = [
    {
      timeframe: "3+ Days",
      price: "$8/page",
      description: "Standard delivery",
      popular: false
    },
    {
      timeframe: "24-48 Hours",
      price: "$12/page",
      description: "Priority handling",
      popular: true
    },
    {
      timeframe: "Under 24 Hours",
      price: "$16/page",
      description: "Rush delivery",
      popular: false
    }
  ];

  const benefits = [
    "100% Plagiarism-Free Work",
    "On-Time Delivery Guaranteed",
    "Unlimited Free Revisions",
    "24/7 Expert Support",
    "Money-Back Promise",
    "Complete Privacy Protection"
  ];

  return (
    <section id="get-help" className="py-20 bg-gradient-to-br from-background via-surface/30 to-background">
      {/* Modals */}
      <ServiceSelectionModal 
        isOpen={showServiceModal} 
        onClose={() => setShowServiceModal(false)} 
        onSelectService={handleServiceSelection} 
      />

      <AssignmentSubmissionForm 
        isOpen={showAssignmentForm} 
        onClose={() => setShowAssignmentForm(false)} 
        onSubmit={() => {
          // Handle successful submission
        }} 
      />

      <ClassManagementForm 
        isOpen={showClassManagementForm} 
        onClose={() => setShowClassManagementForm(false)} 
        onSubmit={() => {
          // Handle successful submission
        }} 
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main CTA Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-6xl font-bold text-text-primary mb-6">
            Choose<span className="text-gradient"> Your Preferred way to get started </span>
          </h2>
          <p className="text-xl md:text-2xl text-text-secondary max-w-4xl mx-auto mb-8">
            Join thousands of students who've discovered the easiest way to get quality academic help. 
            Start your assignment now with transparent pricing and instant expert support.
          </p>

          {/* Live Stats */}
          <div className="flex flex-wrap justify-center items-center space-x-8 mb-8 text-text-muted">
            <div className="flex items-center space-x-2 mb-4">
              <div className="w-3 h-3 bg-success rounded-full animate-pulse"></div>
              <span className="text-sm">{onlineExperts} Experts Online</span>
            </div>
            <div className="flex items-center space-x-2 mb-4">
              <Icon name="CheckCircle" size={16} className="text-accent" />
              <span className="text-sm">{todayOrders} Orders Today</span>
            </div>
            <div className="flex items-center space-x-2 mb-4">
              <Icon name="Clock" size={16} className="text-primary" />
              <span className="text-sm">{currentTime.toLocaleTimeString()}</span>
            </div>
          </div>
        </motion.div>

        {/* Quick Pricing Options */}
        {/*
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16"
        >
          {urgencyOptions.map((option, index) => (
            <motion.div
              key={option.timeframe}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              className={`card-elevated p-6 text-center hover:transform hover:-translate-y-2 transition-all duration-250 relative overflow-visible ${
                option.popular ? 'border-2 border-secondary' : ''
              }`}
            >
              {option.popular && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-20">
                  <div className="bg-secondary text-white px-3 py-1 rounded-full text-xs font-semibold animate-pulse shadow-lg">
                    Most Popular
                  </div>
                </div>
              )}

              <div className="text-lg font-semibold text-text-primary mb-2">
                {option.timeframe}
              </div>
              <div className="text-3xl font-bold text-gradient mb-2">
                {option.price}
              </div>
              <div className="text-text-secondary text-sm mb-4">
                {option.description}
              </div>
              <button
                onClick={() => {
                  const phoneNumber = '254793863320';
                  const message = encodeURIComponent(`Hi! I need help with a ${option.timeframe.toLowerCase()} deadline at ${option.price}. Can you get me started?`);
                  window.open(`https://wa.me/${phoneNumber}?text=${message}`, '_blank');
                }}
                className={`w-full py-2 px-4 rounded-lg text-sm font-medium transition-colors ${
                  option.popular
                    ? 'bg-secondary hover:bg-secondary-600 text-white' :'bg-primary hover:bg-primary-700 text-white'
                }`}
              >
                Get Started Now
              </button>
            </motion.div>
          ))}
        </motion.div>
        */}

        {/* Contact Options */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="mb-16"
        >

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 pt-6 px-2">
            {contactOptions.map((option, index) => (
              <motion.div
                key={option.platform}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="card-elevated p-6 text-center hover:transform hover:-translate-y-2 transition-all duration-250"
              >
                <div className={`w-16 h-16 bg-gradient-to-br from-${option.color}/20 to-${option.color}/30 rounded-full flex items-center justify-center mx-auto mb-4`}>
                  <Icon name={option.icon} size={32} className={`text-${option.color}`} />
                </div>

                <h4 className="text-xl font-semibold text-text-primary mb-2">
                  {option.platform}
                </h4>

                <p className="text-text-secondary mb-3">
                  {option.description}
                </p>

                <div className={`text-sm text-${option.color} font-medium mb-4`}>
                  Response time: {option.responseTime}
                </div>

                {option.platform === "WhatsApp" ? (
                  <motion.button
                    onClick={option.action}
                    whileHover={{ 
                      scale: 1.02,
                      boxShadow: "0 10px 25px rgba(37, 211, 102, 0.3)"
                    }}
                    whileTap={{ 
                      scale: 0.98,
                      transition: { duration: 0.1 }
                    }}
                    className="relative w-full py-3 px-6 bg-gradient-to-r from-[#25D366] to-[#128C7E] hover:from-[#128C7E] hover:to-[#25D366] text-white rounded-lg shadow-lg font-semibold transition-all duration-300 overflow-hidden group inline-flex items-center justify-center space-x-2"
                  >
                    <div className="absolute inset-0 bg-white/10 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700 ease-out"></div>
                    <WhatsAppIcon size={20} className="relative z-10 text-white" />
                    <span className="relative z-10">Contact via {option.platform}</span>
                    <div className="absolute inset-0 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-shimmer"></div>
                    </div>
                  </motion.button>
                ) : (
                  <button
                    onClick={option.action}
                    className={`cta-button w-full py-3 px-6 rounded-lg font-semibold transition-all duration-250 ease-smooth bg-${option.color} hover:bg-${option.color}-600 text-white`}
                  >
                    Contact via {option.platform}
                  </button>
                )}
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Benefits Reminder */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="bg-gradient-to-r from-primary/10 to-secondary/10 rounded-2xl p-8 border border-primary/20 mb-16"
        >
          <h3 className="text-2xl font-bold text-text-primary text-center mb-8">
            What You Get with Every Order
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="flex items-center space-x-3"
              >
                <Icon name="Check" size={20} className="text-success flex-shrink-0" />
                <span className="text-text-secondary">{benefit}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Final CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <h3 className="text-3xl font-bold text-text-primary mb-6">
            Don't Let Academic Stress Hold You Back
          </h3>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto mb-8">
            Thousands of students have already discovered the peace of mind that comes with reliable, 
            affordable academic assistance. Join them today and focus on what matters most - your learning and success.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6">
            <button
              onClick={() => handleButtonClick('assignment')}
              className="cta-button btn-primary text-xl px-10 py-5 inline-flex items-center space-x-3 animate-pulse-slow"
            >
              <Icon name="Zap" size={28} />
              <span>Start Your Assignment Now</span>
            </button>

            <div className="text-text-muted text-sm">
              <div className="flex items-center justify-center space-x-2">
                <Icon name="Shield" size={16} className="text-success" />
                <span>Risk-free with money-back guarantee</span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Footer */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mt-20 pt-12 border-t border-border"
        >
          <div className="flex items-center justify-center space-x-3 mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-primary to-secondary rounded-xl flex items-center justify-center">
              <Icon name="GraduationCap" size={24} color="currentColor" />
            </div>
            <h4 className="text-2xl font-bold text-gradient font-accent">
              SOMO
            </h4>
          </div>
          <p className="text-text-secondary mb-4">
            Transparent, Affordable, Reliable Academic Assistance
          </p>
          <div className="text-text-muted text-sm">
            © {new Date().getFullYear()} SOMO. All rights reserved. |
            <span className="ml-2">Your success is our mission.</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default CTASection;
